// services/documentService.ts - Quick fix for store compatibility

const API_BASE_URL = 'http://localhost:8000/api';

export interface Document {
  _id: string;
  filename: string;
  document_type: string;
  upload_date: string;
  processed_date: string;
  status: "processing" | "uploaded" | "processed" | "failed";
  ocr_data?: {
    extracted_text: string;
  };
  document_metadata?: {
    project_name?: string;
    owner?: string;
    contractor?: string;
    location?: string;
    project_type?: string;
  };
  // Required properties for store compatibility
  file_path: string;
  file_size: number;
  mime_type: string;
  original_filename: string;
  page_count: number;
  extraction_method: string;
  extraction_date: string;
  text_length: number;
}

export interface DocumentUploadResponse {
  success: boolean;
  data: Document;
  message?: string;
}

export interface RiskAnalysis {
  _id: string;
  document_id: string;
  risk_level: string;
  enhanced_risk_score: number;
  recommendation: string;
  risk_factors: string[];
  financial_exposure?: number;
}

class DocumentService {
  // Helper function to map API status to store status
  private mapStatus(apiStatus: string): "processing" | "uploaded" | "processed" | "failed" {
    switch (apiStatus?.toLowerCase()) {
      case 'completed':
      case 'enhanced_processed':
      case 'processed':
        return 'processed';
      case 'processing':
        return 'processing';
      case 'uploaded':
        return 'uploaded';
      case 'failed':
      case 'error':
        return 'failed';
      default:
        return 'uploaded';
    }
  }

  async getAllDocuments(): Promise<Document[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      
      // Ensure all documents have required fields for store compatibility
      return (data.data || []).map((doc: any) => ({
        ...doc,
        status: this.mapStatus(doc.status),
        file_path: doc.file_path || doc.raw_document?.file_path || 'unknown',
        file_size: doc.file_size || doc.raw_document?.file_size || 0,
        mime_type: doc.mime_type || doc.raw_document?.mime_type || 'application/pdf',
        original_filename: doc.original_filename || doc.raw_document?.original_filename || doc.filename,
        page_count: doc.page_count || doc.ocr_data?.page_count || 1,
        extraction_method: doc.extraction_method || doc.ocr_data?.extraction_method || 'unknown',
        extraction_date: doc.extraction_date || doc.ocr_data?.extraction_date || doc.upload_date,
        text_length: doc.text_length || doc.ocr_data?.text_length || 0
      }));
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  async getDocumentById(id: string): Promise<Document | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      
      if (!data.data) return null;
      
      const doc = data.data;
      // Ensure document has required fields for store compatibility
      return {
        ...doc,
        status: this.mapStatus(doc.status),
        file_path: doc.file_path || doc.raw_document?.file_path || 'unknown',
        file_size: doc.file_size || doc.raw_document?.file_size || 0,
        mime_type: doc.mime_type || doc.raw_document?.mime_type || 'application/pdf',
        original_filename: doc.original_filename || doc.raw_document?.original_filename || doc.filename,
        page_count: doc.page_count || doc.ocr_data?.page_count || 1,
        extraction_method: doc.extraction_method || doc.ocr_data?.extraction_method || 'unknown',
        extraction_date: doc.extraction_date || doc.ocr_data?.extraction_date || doc.upload_date,
        text_length: doc.text_length || doc.ocr_data?.text_length || 0
      };
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }

  // Add this method for store compatibility
  async getDocumentAnalysis(documentId: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/risk-analysis`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching document analysis:', error);
      return [];
    }
  }

  async uploadDocument(
    file: File, 
    onProgress?: (progress: any) => void,
    metadata?: any
  ): Promise<DocumentUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const response = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress(100); // Simple completion callback
      }
      
      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  async deleteDocument(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  async getRiskAnalyses(): Promise<RiskAnalysis[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/risk-analyses`);
      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching risk analyses:', error);
      return [];
    }
  }

  async getRiskAnalysisForDocument(documentId: string): Promise<RiskAnalysis[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/risk-analysis`);
      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching document risk analysis:', error);
      return [];
    }
  }

  // Utility functions for risk calculations
  getRiskColor(score: number): string {
    if (score >= 7) return '#dc2626'; // Red
    if (score >= 4) return '#d97706'; // Orange
    return '#059669'; // Green
  }

  getRiskLevel(score: number): string {
    if (score >= 7) return 'High';
    if (score >= 4) return 'Medium';
    return 'Low';
  }

  calculateProjectRiskMetrics(riskAnalyses: RiskAnalysis[]) {
    const totalRisks = riskAnalyses.length;
    const highRisks = riskAnalyses.filter(r => r.enhanced_risk_score >= 7).length;
    const mediumRisks = riskAnalyses.filter(r => r.enhanced_risk_score >= 4 && r.enhanced_risk_score < 7).length;
    const lowRisks = riskAnalyses.filter(r => r.enhanced_risk_score < 4).length;
    
    const averageRisk = totalRisks > 0 
      ? riskAnalyses.reduce((sum, r) => sum + r.enhanced_risk_score, 0) / totalRisks 
      : 0;
      
    const totalFinancialExposure = riskAnalyses
      .filter(r => r.financial_exposure)
      .reduce((sum, r) => sum + (r.financial_exposure || 0), 0);

    return {
      totalRisks,
      highRisks,
      mediumRisks,
      lowRisks,
      averageRisk,
      totalFinancialExposure
    };
  }
}

export const documentService = new DocumentService();